/**
 * The delivery book.
 *
 * One screen, worked from the top down: WHICH deliveries (channel, country,
 * state), WHAT to look at (the book, its totals, the riders, or a new one),
 * WHERE in the day's work (today, active, completed, everything), and then
 * the rows. Every control narrows the same query, so the cards, the list and
 * the totals always describe the same set of deliveries.
 *
 * Nothing here widens what anybody may see. Country and state are built from
 * the locations the API already returned for this login, and row level
 * security applies the same set to every query behind them — a selector can
 * only make the view smaller.
 *
 * Two kinds of row sit in the book. A delivery recorded in CenoX is owned here
 * and can be opened, corrected and cancelled. One mirrored from dispatch is
 * shown but not edited — it is corrected where it is run. Either way the row
 * says where it came from, so a job entered twice is visible rather than
 * merely present.
 */

import { useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Plus } from 'lucide-react';
import {
  DELIVERY_ORIGIN_LABELS,
  DELIVERY_PAYMENT_STATUSES,
  DELIVERY_STATUSES,
  deliveryAge,
  hasCollectibleBalance,
  hasOutstandingPayment,
  isOperationallyOpen,
  openForLabel,
  type Delivery,
  type DeliveryAge,
  type DeliveryCounts,
  type DeliveryOrigin,
  type DeliverySort,
  type DeliveryStage,
  type SlaRuleView,
} from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import {
  useLocationFilter,
  useLocationReady,
  useRecordLocation,
  useWorkingLocation,
} from '../../lib/locationContext';
import { count, date, money } from '../../lib/format';
import { Badge, cn, ErrorState, Modal, Pagination, Spinner } from '../../components/ui';
import { DeliveryEntryForm } from './DeliveryEntryForm';
import { ControlBadges } from './ControlBadges';
import { DeliveryTotals } from './DeliveryTotals';

interface Page {
  data: Delivery[];
  page: number;
  pageSize: number;
  total: number;
}

/** Held events, surfaced so a stalled delivery is visible rather than silent. */
interface Blocked {
  externalRef: string;
  expectedVersion: number;
  receivedVersion: number;
  firstSeenAt: string;
  eventCount: number;
}


/*
  The channels, named as the business names them.

  Shop, App and WhatsApp are the three a customer actually uses. Manual entry
  and "not recorded" follow because they are real values on real rows: a job
  typed in at a CenoX screen is not a Shop job unless the Shop recorded it,
  and a channel nobody wrote down is not any channel. Leaving them off would
  make those deliveries the ones a selector cannot reach.
*/
const SOURCES: Array<{ value: '' | DeliveryOrigin; label: string }> = [
  { value: '', label: 'All Sources' },
  { value: 'shop', label: 'Shop' },
  { value: 'customer_app', label: 'App' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'manual', label: 'Manual entry' },
  { value: 'unknown', label: 'Source not recorded' },
];

const STAGES: Array<{ key: DeliveryStage; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'history', label: 'History' },
];

type View = 'deliveries' | 'totals';

interface Filters {
  search: string;
  deliveryStatus: string;
  paymentStatus: string;
  from: string;
  to: string;
}

const NO_FILTERS: Filters = { search: '', deliveryStatus: '', paymentStatus: '', from: '', to: '' };

export function DeliveriesPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const navigate = useNavigate();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'UTC';
  const locationReady = useLocationReady();
  const geo = useWorkingLocation();
  const { locations } = geo;
  const working = useLocationFilter();
  // A delivery happens somewhere. The same resolution every other form uses.
  const entryLocation = useRecordLocation();

  const [view, setView] = useState<View>('deliveries');
  const [source, setSource] = useState<'' | DeliveryOrigin>('');
  /*
    Country, state and location are the sidebar's — one shared selection, so
    this screen and every other agree on what "Enugu" covers. The controls
    below read and write it; there is no second, page-only geography.
  */
  const country = geo.selection.country ?? '';
  const state = geo.selection.region ?? '';
  const [stage, setStage] = useState<DeliveryStage>('history');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<{ by: DeliverySort; dir: 'asc' | 'desc' }>({
    by: 'date',
    dir: 'desc',
  });
  const [page, setPage] = useState(1);
  const [entering, setEntering] = useState(false);

  // ── Country and state, from the locations this login may see ──────────────

  const stateCountry = geo.regionCountry ?? '';
  const states = geo.regions;
  const inScope = geo.scopedLocations;
  const branchId = geo.selection.branchId ?? '';

  // Everything that decides WHICH deliveries — shared by cards, list and totals.
  const where = {
    ...(source ? { originSource: source } : {}),
    // The shared geographic scope: one location, or an area the API narrows to.
    ...working,
  };
  const scope = {
    ...where,
    ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
    ...(filters.deliveryStatus ? { deliveryStatus: filters.deliveryStatus } : {}),
    ...(filters.paymentStatus ? { paymentStatus: filters.paymentStatus } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };

  const counts = useQuery({
    queryKey: ['deliveries', 'counts', business?.businessId, scope],
    queryFn: () => api.get<DeliveryCounts>(path(`/deliveries/counts${qs(scope)}`)),
    enabled: Boolean(business) && locationReady,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const list = useQuery({
    queryKey: ['deliveries', business?.businessId, page, stage, sort, scope],
    queryFn: () =>
      api.get<Page>(
        path(
          `/deliveries${qs({
            page,
            pageSize: 25,
            sort: sort.by,
            dir: sort.dir,
            ...(stage === 'history' ? {} : { stage }),
            ...scope,
          })}`,
        ),
      ),
    enabled: Boolean(business) && locationReady && view === 'deliveries',
    // Re-sorting, paging or narrowing keeps the rows in place until the answer
    // arrives, instead of blanking the table on every tap.
    placeholderData: keepPreviousData,
  });

  /*
    The business's own deadlines, loaded once for the whole page.

    Resolved in the browser rather than per row on the server: a business has a
    handful of these and the list has hundreds of rows, so this is one request
    instead of a join on every page of deliveries. `deliveryAge` is shared with
    the API so both halves answer "is this late" the same way.
  */
  const slaRules = useQuery({
    queryKey: ['delivery-sla-rules', business?.businessId],
    queryFn: () => api.get<{ data: SlaRuleView[] }>(path('/deliveries/sla-rules')),
    enabled: Boolean(business),
    staleTime: 60_000,
  });

  const blocked = useQuery({
    queryKey: ['deliveries-blocked', business?.businessId],
    queryFn: () => api.get<{ data: Blocked[] }>(path('/deliveries/blocked')),
    enabled: Boolean(business),
  });

  // Every change of scope starts from the first page; page 4 of a different
  // question is usually empty and always misleading.
  const reset = (): void => setPage(1);
  const setFilter = (patch: Partial<Filters>): void => {
    setFilters((current) => ({ ...current, ...patch }));
    reset();
  };


  const toggleSort = (by: DeliverySort): void => {
    setSort((current) =>
      current.by === by
        ? { by, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { by, dir: by === 'reference' || by === 'type' ? 'asc' : 'desc' },
    );
    reset();
  };

  const rows = list.data?.data ?? [];
  const overdue = counts.data?.overdue ?? 0;
  const stuck = blocked.data?.data ?? [];
  const canSeeMoney = can('reports:read');
  const canRecord = can('deliveries:manage') && !entryLocation.unresolved;
  const tzOf = (branch: string): string =>
    locations.find((l) => l.id === branch)?.timezone ?? timezone;
  /*
    Where a delivery happened decides which deadline applies, and a delivery
    happens at a location — so the scope comes from its branch (which already
    falls back to the business's own country and state) rather than from the
    business, which would price every branch the same.
  */
  const ageOf = (d: Delivery): DeliveryAge | null => {
    const rules = slaRules.data?.data;
    if (!rules || !isOperationallyOpen(d.deliveryStatus)) return null;
    const at = locations.find((l) => l.id === d.branchId);
    return deliveryAge(
      {
        deliveryStatus: d.deliveryStatus,
        serviceType: d.serviceType,
        countryCode: at?.countryCode ?? business?.countryCode ?? null,
        stateCode: at?.stateCode ?? null,
        openedAt: d.postedAt ?? d.createdAt,
      },
      rules,
      new Date(),
    );
  };
  const filtersOn =
    Object.values(filters).filter((v) => v.trim() !== '').length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Every job, where it happened, and the money it produced.
      </p>

      {/* ── Which deliveries ─────────────────────────────────────────────── */}
      <div className="space-y-2.5">
        <ScopeSelect
          label="Delivery source"
          value={source}
          onChange={(v) => {
            setSource(v as '' | DeliveryOrigin);
            reset();
          }}
          // The prompt shows while nothing narrows the list; "All Sources" is
          // the same choice, named, for somebody undoing a filter.
          options={[{ value: '', label: 'Select (Shop/App/WhatsApp)', hidden: true }, ...SOURCES]}
          muted={source === ''}
        />
        <div className="grid grid-cols-2 gap-2.5">
          <ScopeSelect
            label="Country"
            value={country}
            disabled={!geo.canAggregate}
            onChange={(v) => {
              // A new country drops the state and location (lib/geoScope.ts).
              geo.setCountry(v || null);
              reset();
            }}
            options={[
              ...(geo.canAggregate ? [{ value: '', label: 'All Countries' }] : []),
              ...geo.countries,
            ]}
          />
          <ScopeSelect
            label={stateCountry ? geo.regionTerm.singular : 'State / Region'}
            value={state}
            disabled={!geo.canAggregate || !stateCountry || states.length === 0}
            onChange={(v) => {
              // A new state drops a location outside it.
              geo.setRegion(v || null);
              reset();
            }}
            options={[
              {
                value: '',
                label: !stateCountry
                  ? 'Choose a country first'
                  : states.length === 0
                    ? `No ${geo.regionTerm.plural.toLowerCase()} listed`
                    : `All ${geo.regionTerm.plural}`,
              },
              ...states,
            ]}
          />
        </div>
      </div>

      {/* ── What to look at ──────────────────────────────────────────────── */}
      <nav aria-label="Deliveries" className="flex gap-2">
        <NavButton active={view === 'deliveries'} onClick={() => setView('deliveries')}>
          Deliveries
        </NavButton>
        {canSeeMoney && (
          <NavButton active={view === 'totals'} onClick={() => setView('totals')}>
            Totals
          </NavButton>
        )}
        {/* Rider management lives in Logistics, behind the same authority. */}
        {canSeeMoney && (
          <NavButton active={false} onClick={() => navigate('/business/logistics/riders')}>
            Riders
          </NavButton>
        )}
        {canRecord && (
          <NavButton active={false} onClick={() => setEntering(true)}>
            <Plus className="size-4" aria-hidden />
            New
          </NavButton>
        )}
      </nav>

      <Modal open={entering} onClose={() => setEntering(false)} title="New delivery" size="lg">
        <DeliveryEntryForm
          branchId={entryLocation.branchId}
          onDone={() => setEntering(false)}
          onCancel={() => setEntering(false)}
        />
      </Modal>

      {/* ── Where in the work ────────────────────────────────────────────── */}
      <div className="grid grid-cols-4 gap-2" role="group" aria-label="Delivery status">
        {STAGES.map((s) => {
          const value = counts.data?.[s.key];
          const selected = view === 'deliveries' && stage === s.key;
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setStage(s.key);
                setView('deliveries');
                reset();
              }}
              className={cn(
                'flex min-h-[4.5rem] flex-col items-center justify-center rounded-xl border bg-white px-1 py-2.5',
                'shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500',
                selected
                  ? 'border-green-500 ring-1 ring-green-500'
                  : 'border-slate-200 hover:border-slate-300',
              )}
            >
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">
                {s.label}
              </span>
              <span
                className={cn(
                  'mt-1 text-lg font-semibold tabular-nums sm:text-2xl',
                  selected ? 'text-green-600' : 'text-slate-900',
                )}
              >
                {value === undefined ? (counts.isError ? '—' : '·') : count(value)}
              </span>
              {/*
                Overdue sits under Active, not beside it as a fifth card: it is
                a property of those jobs, not another bucket of work, and a
                card of its own would be counted twice by anybody adding up.
              */}
              {s.key === 'active' && overdue > 0 && (
                <span className="mt-0.5 text-[11px] font-semibold text-red-600">
                  {count(overdue)} overdue
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/*
        What Active is made of.
        Shown under the cards rather than on the card, because these are two
        different kinds of unfinished business and an owner acts on them
        differently: one needs a rider chased, the other needs money collected.
        Only while Active is the chosen card — on Completed it would be noise.
      */}
      {view === 'deliveries' && stage === 'active' && counts.data && (
        <ActiveBreakdown counts={counts.data} currency={currency} />
      )}

      {/* ── Filters & search ─────────────────────────────────────────────── */}
      <section className="overflow-hidden rounded-xl bg-[#0b1b3f] text-white shadow-sm">
        <button
          type="button"
          aria-expanded={filtersOpen}
          aria-controls="delivery-filters"
          onClick={() => setFiltersOpen((open) => !open)}
          className="flex w-full items-center gap-2 px-4 py-3.5 text-left text-sm font-semibold"
        >
          <ChevronRight
            className={cn('size-4 transition-transform', filtersOpen && 'rotate-90')}
            aria-hidden
          />
          Filters &amp; search
          {filtersOn > 0 && (
            <span className="ml-auto rounded-full bg-green-500 px-2 py-0.5 text-xs font-semibold">
              {filtersOn} on
            </span>
          )}
        </button>
        {filtersOpen && (
          <div id="delivery-filters" className="space-y-3 border-t border-white/10 px-4 pb-4 pt-3">
            <DarkField label="Search">
              <input
                type="search"
                value={filters.search}
                onChange={(e) => setFilter({ search: e.target.value })}
                placeholder="Reference, sender, receiver, rider name or ID, phone"
                className={DARK_INPUT}
              />
            </DarkField>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <DarkField label="Delivery status">
                <select
                  value={filters.deliveryStatus}
                  onChange={(e) => setFilter({ deliveryStatus: e.target.value })}
                  className={DARK_INPUT}
                >
                  <option value="">All statuses</option>
                  {DELIVERY_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                  {/* What imported and app records write for a finished job. */}
                  <option value="Completed">Completed</option>
                </select>
              </DarkField>
              <DarkField label="Payment status">
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => setFilter({ paymentStatus: e.target.value })}
                  className={DARK_INPUT}
                >
                  <option value="">All payment statuses</option>
                  {DELIVERY_PAYMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </DarkField>
              <DarkField label="Location">
                <select
                  value={branchId}
                  onChange={(e) => {
                    geo.setBranchId(e.target.value || null);
                    reset();
                  }}
                  className={DARK_INPUT}
                >
                  {geo.canAggregate && <option value="">All locations</option>}
                  {inScope.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </DarkField>
              <DarkField label="From">
                <input
                  type="date"
                  value={filters.from}
                  max={filters.to || undefined}
                  onChange={(e) => setFilter({ from: e.target.value })}
                  className={DARK_INPUT}
                />
              </DarkField>
              <DarkField label="To">
                <input
                  type="date"
                  value={filters.to}
                  min={filters.from || undefined}
                  onChange={(e) => setFilter({ to: e.target.value })}
                  className={DARK_INPUT}
                />
              </DarkField>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    // Geography stays: it is the sidebar's, shared by every screen.
                    setFilters(NO_FILTERS);
                    reset();
                  }}
                  className="h-10 w-full rounded-lg border border-white/20 px-3 text-sm font-medium text-white/90 hover:bg-white/10"
                >
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/*
        A held event means a delivery has quietly stopped updating: an earlier
        event never arrived, so later ones wait rather than apply out of order.
        Nobody would notice that on their own, so it is said out loud.
      */}
      {stuck.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" role="alert">
          <p className="text-sm font-medium text-amber-900">
            {stuck.length} delivery update{stuck.length === 1 ? '' : 's'} waiting on a missing
            earlier event
          </p>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {stuck.slice(0, 5).map((b) => (
              <li key={b.externalRef}>
                <span className="font-mono">{b.externalRef}</span> — expected version{' '}
                {b.expectedVersion}, received {b.receivedVersion}, first seen{' '}
                {date(b.firstSeenAt, timezone)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {view === 'totals' && canSeeMoney ? (
        <DeliveryTotals
          scope={where}
          from={filters.from}
          to={filters.to}
          onRange={(from, to) => setFilter({ from, to })}
        />
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <header className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-4">
            <h2 className="text-base font-semibold text-slate-900">Deliveries</h2>
            {list.data && (
              <span className="text-xs text-slate-500">
                {count(list.data.total)} {list.data.total === 1 ? 'delivery' : 'deliveries'}
              </span>
            )}
          </header>

          {list.isLoading && (
            <div className="p-4">
              <Spinner label="Loading deliveries" />
            </div>
          )}
          {list.isError && (
            <div className="p-4">
              <ErrorState message="Could not load deliveries." onRetry={() => void list.refetch()} />
            </div>
          )}

          {list.data && rows.length === 0 && (
            <div className="px-4 pb-10 pt-6 text-center">
              <p className="text-sm font-medium text-slate-900">No deliveries found.</p>
              <p className="mt-1 text-sm text-slate-500">Try adjusting your filters or search.</p>
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto">
              {/* Fits a phone without sideways scrolling; scrolls only below ~360px. */}
              <table className="w-full min-w-[22rem] text-sm">
                <thead>
                  <tr className="border-y border-slate-200 bg-slate-50 text-left text-xs text-slate-500">
                    <SortHeader label="Date & time" by="date" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Order / Reference" by="reference" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Type" by="type" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Amount" by="amount" sort={sort} onSort={toggleSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => (
                    <DeliveryRow
                      key={d.id}
                      delivery={d}
                      currency={d.currencyCode ?? currency}
                      timezone={tzOf(d.branchId)}
                      age={ageOf(d)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {list.data && list.data.total > list.data.pageSize && (
            <div className="px-4 pb-4">
              <Pagination
                page={list.data.page}
                pageSize={list.data.pageSize}
                total={list.data.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function DeliveryRow({
  delivery: d,
  currency,
  timezone,
  age,
}: {
  delivery: Delivery;
  currency: string;
  timezone: string;
  /** Null for a job that is no longer moving — a finished job has no age. */
  age: DeliveryAge | null;
}): ReactNode {
  const cancelled = (d.deliveryStatus ?? '').toLowerCase().startsWith('cancel');
  return (
    <tr className="border-b border-slate-100 align-top last:border-0">
      <td className="px-2 py-3 text-xs text-slate-600 sm:px-4">
        {/* Date over time on a phone; one line on a desk. */}
        <span className="whitespace-nowrap">{date(d.postedAt ?? d.createdAt, timezone)}</span>{' '}
        <span className="block whitespace-nowrap text-slate-500 sm:inline">
          {time(d.postedAt ?? d.createdAt, timezone)}
        </span>
        <div className="mt-0.5 text-slate-400">{d.branchName ?? ''}</div>
      </td>
      <td className="px-2 py-3 sm:px-4">
        <Link
          to={`/deliveries/${d.id}`}
          className="break-all font-mono text-xs font-medium text-brand-700 hover:underline"
        >
          {d.externalRef}
        </Link>
        {/* The customer's order number, when it is not the reference — searchable too. */}
        {d.dispatchRef && d.dispatchRef !== d.externalRef && (
          <div className="break-all font-mono text-xs text-slate-600">{d.dispatchRef}</div>
        )}
        {d.feeReview?.status === 'needs_review' && (
          <span className="mr-1 mt-0.5 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-800">
            {d.feeReview.kind === 'fee_mismatch' ? 'Fee mismatch — needs review' : 'Duplicate reference — needs review'}
          </span>
        )}
        {d.deliveryType && (
          <span
            className={cn(
              'mt-0.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium',
              d.deliveryType === 'fast' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600',
            )}
          >
            {d.deliveryType === 'fast' ? 'Fast' : 'Base'}
          </span>
        )}
        {/* Sender → receiver, then where it went. */}
        <div className="mt-0.5 text-xs text-slate-700">
          {[d.customerName, d.receiverName].filter(Boolean).join(' → ') || '—'}
        </div>
        {(d.pickup || d.destination) && (
          <div className="text-xs text-slate-500">
            {[d.pickup, d.destination].filter(Boolean).join(' → ')}
          </div>
        )}
        {d.riderName && (
          <div className="hidden text-xs text-slate-400 sm:block">
            Rider: <span>{d.riderName}</span>
            {d.riderExternalId ? ` · ${d.riderExternalId}` : ''}
          </div>
        )}
        {/* The financial records it produced, reachable from the row. */}
        {(d.saleReference || d.expenseReference) && (
          <div className="mt-0.5 hidden flex-wrap gap-x-2 text-xs sm:flex">
            {d.saleReference && (
              <Link
                to={`/sales?search=${encodeURIComponent(d.saleReference)}`}
                className="text-brand-700 hover:underline"
              >
                {d.saleReference}
              </Link>
            )}
            {d.expenseReference && <span className="text-slate-500">{d.expenseReference}</span>}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1">
          <Badge tone={cancelled ? 'danger' : 'success'}>{d.deliveryStatus ?? 'Unknown'}</Badge>
          <Badge tone={d.paymentStatus === 'Paid' ? 'success' : 'neutral'}>
            {d.paymentStatus ?? 'No payment status'}
          </Badge>
          <ControlBadges delivery={d} />
          <LedgerMismatch delivery={d} />
          {age && <AgeBadges age={age} />}
        </div>
      </td>
      <td className="px-2 py-3 text-xs sm:px-4">
        <div className="font-medium text-slate-800">
          {DELIVERY_ORIGIN_LABELS[d.originSource] ?? d.originSource}
        </div>
        <div className="hidden text-slate-400 sm:block">
          {[d.serviceType, d.recordOrigin === 'import' ? 'Imported' : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {/* Who entered it: the first question about a record somebody else made. */}
        {d.recordedByName && (
          <div className="hidden text-slate-400 sm:block">by {d.recordedByName}</div>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-3 text-right sm:px-4">
        <div className="font-semibold tabular-nums text-slate-900">{money(d.fee, currency)}</div>
        {hasCollectibleBalance(d) && <Owed delivery={d} currency={currency} />}
        {/* What the rider took and what the business kept — shown apart, never added. */}
        <div className="hidden text-xs tabular-nums text-slate-400 sm:block">
          Rider {money(d.riderPayout, currency)}
        </div>
        <div className="text-xs tabular-nums text-slate-500">
          Kept {money(d.companyShare, currency)}
        </div>
      </td>
    </tr>
  );
}

/**
 * Says so when the payment status and the sales ledger disagree.
 *
 * The cards classify by payment status, which is the field a counter actually
 * sets and the only one every delivery has. That is a decision, not an
 * assumption — so where there IS an authoritative balance and it contradicts
 * the status, the row says which two things disagree instead of quietly
 * picking one. A job marked Paid that the ledger still shows owing is either a
 * missed payment record or a wrong status, and both need a person.
 *
 * Silent where `ledgerOutstanding` is null: no sale, or a voided one, is not
 * evidence of anything to contradict.
 */
function LedgerMismatch({ delivery: d }: { delivery: Delivery }): ReactNode {
  if (d.ledgerOutstanding === null) return null;

  const owedOnLedger = Number(d.ledgerOutstanding) > 0;
  const owedByStatus = hasOutstandingPayment(d.paymentStatus);
  if (owedOnLedger === owedByStatus) return null;

  return (
    <Badge tone="warning">
      {owedOnLedger ? 'Marked paid, ledger still owing' : 'Marked unpaid, ledger settled'}
    </Badge>
  );
}

/**
 * What is still owed on a job, where there is an authoritative figure for it.
 *
 * `ledgerOutstanding` comes from the sale and its payments — the Money
 * report's own expression. Where it is null there is nothing authoritative to
 * read: no sale was posted, or the sale was voided. An unpaid job with no
 * ledger still has an unambiguous answer, which is its fee: nothing has been
 * collected, and that is the basis the Logistics receivable already uses.
 *
 * A part-paid job with no ledger is the one case with no honest number. It
 * says so. Showing the fee there would overstate the debt by whatever was
 * already handed over, and showing zero would erase it — so it shows neither.
 */
function Owed({ delivery: d, currency }: { delivery: Delivery; currency: string }): ReactNode {
  const partPaid = (d.paymentStatus ?? '').trim().toLowerCase() === 'part payment';

  if (d.ledgerOutstanding !== null) {
    return (
      <div className="text-xs font-medium tabular-nums text-amber-700">
        Owed {money(d.ledgerOutstanding, currency)}
      </div>
    );
  }
  if (!partPaid) {
    return (
      <div className="text-xs font-medium tabular-nums text-amber-700">
        Owed {money(d.fee, currency)}
      </div>
    );
  }
  return <div className="text-xs text-slate-500">Balance not on the ledger</div>;
}

/**
 * The two halves of Active, and what the second one is worth.
 *
 * `inProgress` + `awaitingPayment` = `active`, and they are disjoint by
 * construction: a job cannot be both still moving and already delivered. The
 * money shown is what is owed on the delivered half — the same basis as the
 * Logistics receivable, so the board and the report never describe one debt
 * with two numbers.
 */
function ActiveBreakdown({
  counts,
  currency,
}: {
  counts: DeliveryCounts;
  currency: string;
}): ReactNode {
  return (
    <dl
      className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-4"
      aria-label="What Active is made of"
    >
      <Figure label="Total active" value={count(counts.active)} />
      <Figure
        label="In progress"
        value={count(counts.inProgress)}
        hint={
          counts.inProgressUnpaid > 0
            ? `${count(counts.inProgressUnpaid)} not yet paid for, counted once`
            : 'with a rider, or waiting for one'
        }
      />
      <Figure
        label="Delivered, awaiting payment"
        value={count(counts.awaitingPayment)}
        hint="still labelled Delivered in the list"
      />
      <Figure
        label="Outstanding"
        value={money(counts.outstandingAmount, currency)}
        tone={Number(counts.outstandingAmount) > 0 ? 'owed' : undefined}
      />
    </dl>
  );
}

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'owed';
}): ReactNode {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-base font-semibold tabular-nums sm:text-lg',
          tone === 'owed' ? 'text-amber-700' : 'text-slate-900',
        )}
      >
        {value}
      </dd>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

/**
 * How long an open job has been open, and whether that is late.
 *
 * Two separate claims, deliberately shown as two separate things. The age is
 * arithmetic and always shown; "Overdue" is a judgement against a deadline the
 * business set, so it appears only when there is one to miss. A business that
 * has configured nothing still sees that a job has been open nine days — it
 * just is not told that nine days is wrong, because nobody said what right was.
 */
function AgeBadges({ age }: { age: DeliveryAge }): ReactNode {
  return (
    <>
      <Badge tone={age.overdue ? 'danger' : 'neutral'}>
        Open {openForLabel(age.openHours)}
      </Badge>
      {age.overdue && (
        <Badge tone="danger">
          Overdue by {openForLabel(age.overdueByHours)}
        </Badge>
      )}
    </>
  );
}

const DARK_INPUT =
  'h-10 w-full rounded-lg border border-white/15 bg-white/10 px-3 text-sm text-white ' +
  'placeholder:text-white/50 focus:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-400/30 ' +
  '[&>option]:text-slate-900 [color-scheme:dark]';

function DarkField({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-white/70">{label}</span>
      {children}
    </label>
  );
}

/** A large, touch-friendly selector. The label is for screen readers; the value speaks for itself. */
function ScopeSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  muted,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string; hidden?: boolean }>;
  disabled?: boolean;
  muted?: boolean;
}): ReactNode {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-12 w-full rounded-xl border border-slate-200 bg-white px-4 pr-9 text-sm font-medium shadow-sm',
          'focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          muted ? 'text-slate-500' : 'text-slate-900',
        )}
      >
        {options.map((o, i) => (
          <option key={`${o.value}-${i}`} value={o.value} hidden={o.hidden}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NavButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-1 rounded-xl px-2 text-sm font-semibold',
        'tracking-tight shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2',
        active
          ? 'bg-green-500 text-white hover:bg-green-600'
          : 'bg-[#0b1b3f] text-white hover:bg-[#13285a]',
      )}
    >
      {children}
    </button>
  );
}

function SortHeader({
  label,
  by,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  by: DeliverySort;
  sort: { by: DeliverySort; dir: 'asc' | 'desc' };
  onSort: (by: DeliverySort) => void;
  align?: 'left' | 'right';
}): ReactNode {
  const active = sort.by === by;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('px-2 py-2.5 font-medium sm:px-4', align === 'right' && 'text-right')}
    >
      <button
        type="button"
        onClick={() => onSort(by)}
        className={cn('inline-flex items-center gap-1 hover:text-slate-900', active && 'text-slate-900')}
      >
        {label}
        <Icon className="size-3.5" aria-hidden />
      </button>
    </th>
  );
}

/** The time of day, in the location's own timezone. */
function time(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: timezone });
}

